import _ from 'underscore-lib';

import _string from 'underscore.string';

var originalUniqueIdFunction = _.uniqueId;
var lastUniqueId = undefined;

/**
 * Mix in non-conflict functions of Underscore.string to Underscore namespace
 */
_.mixin(_string.exports());

// attach full underscore.string to underscore to give access to all string functions, even the ones that otherwise
// conflict with existing underscore functions
_.string = _string;

_.mixin({
    /**
     * A proxy to underscore's original unqiueId function but which also saves the most recent generated value for
     * lastUniqueId() to return
     */
    uniqueId: function (prefix) {
        lastUniqueId = originalUniqueIdFunction(prefix);
        return lastUniqueId;
    },

    /**
     * Returns the most recent value generated with uniqueId().  Useful when needing to use an id twice in an html
     * template (e.g. for a checkbox's "id" attribute and then the label's "for" attribute) without having to first
     * assign the uniqueId() result to a variable.
     */
    lastUniqueId: function () {
        return lastUniqueId;
    },

    /**
     * Make an object immutable by recursively freezing each property which is of type object.
     * The given object should not contain cycles in the reference graph, otherwise an endless loop will be triggered.
     * @param obj - The object to deep freeze
     */
    deepFreeze: function deepFreeze(obj) {
        // Retrieve the property names defined on obj
        let propNames = Object.getOwnPropertyNames(obj);

        // Freeze properties before freezing self
        propNames.forEach(function (name) {
            let prop = obj[name];

            // Freeze prop if it is an object
            if (typeof prop === 'object' && prop !== null) {
                deepFreeze(prop);
            }
        });

        // Freeze self (no-op if already frozen)
        return Object.freeze(obj);
    },

    /**
     * A version of underscore's pick function (http://underscorejs.org/#pick) which works on a
     * collection of objects instead of just a single object.  Returns an array of copies of the
     * provided objects, each filtered to only have values for the provided whitelisted keys
     *
     * @param array an array of objects to pick the provided keys from
     * @param keys the keys to pick from the provided objects, either as an array or varargs
     */
    pickArray: function (array) {
        var _ = this;
        var keysArguments = Array.prototype.slice.call(arguments, 1);
        var flattenedKeysArray = [].concat.apply([], keysArguments);
        return this.map(array, function (item) {
            return _.pick(item, flattenedKeysArray);
        });
    },

    /**
     * An alternative to setInterval that takes into account whether the callback is still running or not and
     * does not ignore errors. With this approach the number of repetitions is set, it does not repeat infinitely.
     * To simulate clearInterval the callback must return boolean if condition is met which will set the timer to 0
     *
     * @param func the code to be executed in the interval
     * @param wait the time to wait between calls (in ms)
     * @param times the number of times to execute func
     */
    /* eslint-disable */
    conditionalInterval: function (func, wait, times) {
        var interv = (function (w, t) {
            var conditionMet = false;
            return function () {
                if (typeof t === 'undefined' || t-- > 0) {
                    setTimeout(interv, w);
                    try {
                        conditionMet = func.call(null);
                        t = conditionMet ? 0 : t;
                    } catch (e) {
                        t = 0;
                        throw e.toString();
                    }
                } else if (!conditionMet) {
                    console.info(
                        'conditionalInterval(' +
                            wait +
                            ',' +
                            times +
                            '): condition was not met in the allocated time interval, giving up now'
                    );
                }
            };
        })(wait, times);
        setTimeout(interv, wait);
    },
    /* eslint-enable */

    /**
     * Replace all occurrences of a sub-string in a string
     * replaceAll("foo", "o", "a"); // => "faa"
     */
    replaceAll: function (str, find, replace, ignorecase) {
        var flags = ignorecase === true ? 'gi' : 'g';
        var reg = new RegExp(find, flags);
        let toReturn = str === null ? '' : String(str);
        return toReturn.replace(reg, replace);
    },

    /**
     * Get a function that will execute either clickCallback / doubleClickCallback. Internally it uses
     * _.debounce to execute one of the given functions on the trailing edge of the wait period.
     * Example:
     * let callback = _.getClickOrDoubleClickCallback(clickCallback, doubleClickCallback);
     * $element.on('click', (ev) => {
     *      callback(ev.data);
     * });
     * @param clickCallback - the handler for single click
     * @param doubleClickCallback - the handler for double click
     * @returns {Function} - accepts one (optional) parameter
     */
    getClickOrDoubleClickCallback: function (clickCallback, doubleClickCallback) {
        let WAIT_TIME = 500; // recommended wait time for detecting double click
        if (!_.isFunction(clickCallback) || !_.isFunction(doubleClickCallback)) {
            throw new Error('Callback functions must be provided');
        }
        let timeoutId = null;
        let clickCount = 0;
        return function (...clickArgs) {
            clickCount += 1;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(function () {
                if (clickCount === 1) {
                    clickCallback(...clickArgs);
                } else if (clickCount === 2) {
                    doubleClickCallback(...clickArgs);
                }
                timeoutId = null;
                clickCount = 0;
            }, WAIT_TIME);
        };
    }
});

export default _.noConflict();
